import React from 'react';
import { token } from '@atlaskit/tokens';
import { type MessageDescriptor, useIntl } from '@atlassian/jira-intl';
import { IllustrationWrapper } from '../../../../common/ui/illustration/illustration-wrapper';
import { ProjectNameText } from '../../../../common/ui/illustration/project-name-text';
import messages from './messages';
import { transformProjectKeyText } from './utils';

export type Props = {
	isTmp: boolean;
	projectName: string;
	projectKey: string;
	teamManagedIllustrationFooter: MessageDescriptor;
	companyManagedIllustrationFooter: MessageDescriptor;
};

export const Illustration = ({
	projectName,
	projectKey,
	isTmp,
	teamManagedIllustrationFooter,
	companyManagedIllustrationFooter,
}: Props) => {
	const { formatMessage } = useIntl();

	return (
		<IllustrationWrapper data-testId="project-create-importer-upload-flow.project-create-screen.illustration.jwm.wrapper">
			<svg
				width="720"
				height="586"
				viewBox="0 0 720 586"
				fill="none"
				xmlns="http://www.w3.org/2000/svg"
				role="img"
				aria-label={formatMessage(messages.illustrationLabelSpork)}
			>
				<title>{formatMessage(messages.illustrationLabelSpork)}</title>
				<path
					fillRule="evenodd"
					clipRule="evenodd"
					d="M370.025 17.9079C434.256 22.6735 503.767 7.79394 556.943 40.6745C610.66 73.8893 624.33 136.807 649.809 190.907C678.228 251.252 739.504 312.537 713.872 373.89C688.153 435.452 591.852 436.828 531.607 473.835C474.196 509.102 439.255 580.301 370.025 585.109C300.167 589.961 241.321 540.007 189.315 497.609C141.938 458.986 117.437 406.978 88.5028 355.603C53.1707 292.869 -12.2428 234.212 1.99552 165.015C16.473 94.6556 86.9077 40.1425 159.374 11.1767C225.234 -15.1486 298.243 12.582 370.025 17.9079Z"
					fill="#2684FF"
				/>
				<g filter="url(#create-project-shadow)">
					<rect
						x="20"
						y="80"
						width="640"
						height="400"
						rx="8"
						fill={token('elevation.surface', 'white')}
						stroke={token('color.background.accent.gray.subtler', '#EBECF0')}
						strokeWidth="1"
						filter="create-project-shadow"
					/>
				</g>
				<rect
					x="42"
					y="142"
					width="20"
					height="20"
					rx="10"
					fill={token('color.background.accent.gray.subtler', '#DFE1E6')}
				/>
				<rect
					x="78"
					y="148"
					width="100"
					height="8"
					rx="4"
					fill={token('color.background.accent.gray.subtler', '#DFE1E6')}
				/>
				<rect
					x="42"
					y="178"
					width="20"
					height="20"
					rx="10"
					fill={token('color.background.accent.gray.subtler', '#DFE1E6')}
				/>
				<rect
					x="78"
					y="184"
					width="100"
					height="8"
					rx="4"
					fill={token('color.background.accent.gray.subtler', '#DFE1E6')}
				/>
				<rect
					x="42"
					y="214"
					width="20"
					height="20"
					rx="10"
					fill={token('color.background.accent.gray.subtler', '#DFE1E6')}
				/>
				<rect
					x="78"
					y="220"
					width="100"
					height="8"
					rx="4"
					fill={token('color.background.accent.gray.subtler', '#DFE1E6')}
				/>
				<rect
					x="42"
					y="250"
					width="20"
					height="20"
					rx="10"
					fill={token('color.background.accent.gray.subtler', '#DFE1E6')}
				/>
				<rect
					x="78"
					y="256"
					width="100"
					height="8"
					rx="4"
					fill={token('color.background.accent.gray.subtler', '#DFE1E6')}
				/>
				<rect
					x="42"
					y="286"
					width="20"
					height="20"
					rx="10"
					fill={token('color.background.accent.gray.subtler', '#DFE1E6')}
				/>
				<rect
					x="78"
					y="292"
					width="100"
					height="8"
					rx="4"
					fill={token('color.background.accent.gray.subtler', '#DFE1E6')}
				/>
				<rect
					x="42"
					y="322"
					width="20"
					height="20"
					rx="10"
					fill={token('color.background.accent.gray.subtler', '#DFE1E6')}
				/>
				<rect
					x="78"
					y="328"
					width="100"
					height="8"
					rx="4"
					fill={token('color.background.accent.gray.subtler', '#DFE1E6')}
				/>
				<rect
					x="42"
					y="358"
					width="20"
					height="20"
					rx="10"
					fill={token('color.background.accent.gray.subtler', '#DFE1E6')}
				/>
				<rect
					x="78"
					y="364"
					width="100"
					height="8"
					rx="4"
					fill={token('color.background.accent.gray.subtler', '#DFE1E6')}
				/>
				<rect x="40" y="100" width="24" height="24" rx="6" fill="#2684FF" />
				<rect
					x="204"
					y="80"
					width="2"
					height="400"
					fill={token('color.border.disabled', '#EBECF0')}
				/>

				<rect
					x="239"
					y="148"
					width="16"
					height="8"
					rx="4"
					fill={token('color.background.accent.gray.subtler', '#DFE1E6')}
				/>
				<rect
					x="271"
					y="132"
					width="2"
					height="334"
					fill={token('color.border.disabled', '#EBECF0')}
				/>
				<rect
					x="285"
					y="148"
					width="64"
					height="8"
					rx="4"
					fill={token('color.background.accent.gray.subtler', '#DFE1E6')}
				/>
				<rect
					x="361"
					y="132"
					width="2"
					height="334"
					fill={token('color.border.disabled', '#EBECF0')}
				/>
				<rect
					x="377"
					y="147"
					width="120"
					height="8"
					rx="4"
					fill={token('color.background.accent.gray.subtler', '#DFE1E6')}
				/>
				<rect
					x="513"
					y="132"
					width="2"
					height="334"
					fill={token('color.border.disabled', '#EBECF0')}
				/>
				<rect
					x="527"
					y="148"
					width="64"
					height="8"
					rx="4"
					fill={token('color.background.accent.gray.subtler', '#DFE1E6')}
				/>
				<rect
					x="603"
					y="132"
					width="2"
					height="334"
					fill={token('color.border.disabled', '#EBECF0')}
				/>
				<rect
					x="613"
					y="148"
					width="24"
					height="8"
					rx="4"
					fill={token('color.background.accent.gray.subtler', '#DFE1E6')}
				/>

				<rect
					x="239"
					y="186"
					width="16"
					height="16"
					rx="4"
					fill={token('color.background.accent.blue.subtle', '#4C9AFF')}
				/>
				<rect
					x="377"
					y="189"
					width="120"
					height="8"
					rx="4"
					fill={token('color.background.accent.gray.subtler', '#DFE1E6')}
				/>
				<rect
					x="527"
					y="186"
					width="64"
					height="16"
					rx="3"
					fill={token('color.background.accent.gray.subtler', '#C1C7D0')}
				/>
				<rect
					x="616"
					y="184"
					width="20"
					height="20"
					rx="10"
					fill={token('color.background.accent.red.subtler', '#FF8F73')}
				/>

				<rect
					x="239"
					y="228"
					width="16"
					height="16"
					rx="4"
					fill={token('color.background.accent.blue.subtle', '#4C9AFF')}
				/>
				<rect
					x="377"
					y="231"
					width="70"
					height="8"
					rx="4"
					fill={token('color.background.accent.gray.subtler', '#DFE1E6')}
				/>
				<rect
					x="527"
					y="228"
					width="64"
					height="16"
					rx="3"
					fill={token('color.background.accent.gray.subtler', '#C1C7D0')}
				/>
				<rect
					x="616"
					y="226"
					width="20"
					height="20"
					rx="10"
					fill={token('color.background.accent.blue.subtler', '#4C9AFF')}
				/>

				<rect
					x="239"
					y="270"
					width="16"
					height="16"
					rx="4"
					fill={token('color.background.accent.blue.subtle', '#4C9AFF')}
				/>
				<rect
					x="377"
					y="273"
					width="120"
					height="8"
					rx="4"
					fill={token('color.background.accent.gray.subtler', '#DFE1E6')}
				/>
				<rect
					x="527"
					y="270"
					width="64"
					height="16"
					rx="3"
					fill={token('color.background.accent.blue.subtler', '#B3D4FF')}
				/>
				<rect
					x="616"
					y="268"
					width="20"
					height="20"
					rx="10"
					fill={token('color.background.accent.blue.subtler', '#4C9AFF')}
				/>

				<rect
					x="239"
					y="312"
					width="16"
					height="16"
					rx="4"
					fill={token('color.background.accent.blue.subtle', '#4C9AFF')}
				/>
				<rect
					x="377"
					y="315"
					width="120"
					height="8"
					rx="4"
					fill={token('color.background.accent.gray.subtler', '#DFE1E6')}
				/>
				<rect
					x="527"
					y="312"
					width="64"
					height="16"
					rx="3"
					fill={token('color.background.accent.blue.subtler', '#B3D4FF')}
				/>
				<rect
					x="616"
					y="310"
					width="20"
					height="20"
					rx="10"
					fill={token('color.background.accent.orange.subtler', '#FFC400')}
				/>

				<rect
					x="239"
					y="354"
					width="16"
					height="16"
					rx="4"
					fill={token('color.background.accent.blue.subtle', '#4C9AFF')}
				/>
				<rect
					x="377"
					y="357"
					width="120"
					height="8"
					rx="4"
					fill={token('color.background.accent.gray.subtler', '#DFE1E6')}
				/>
				<rect
					x="527"
					y="354"
					width="64"
					height="16"
					rx="3"
					fill={token('color.background.accent.blue.subtler', '#B3D4FF')}
				/>
				<rect
					x="616"
					y="352"
					width="20"
					height="20"
					rx="10"
					fill={token('color.background.accent.red.subtler', '#FF8F73')}
				/>

				<rect
					x="239"
					y="396"
					width="16"
					height="16"
					rx="4"
					fill={token('color.background.accent.blue.subtle', '#4C9AFF')}
				/>
				<rect
					x="377"
					y="399"
					width="120"
					height="8"
					rx="4"
					fill={token('color.background.accent.gray.subtler', '#DFE1E6')}
				/>
				<rect
					x="527"
					y="396"
					width="64"
					height="16"
					rx="3"
					fill={token('color.background.accent.green.subtler', '#ABF5D1')}
				/>
				<rect
					x="616"
					y="394"
					width="20"
					height="20"
					rx="10"
					fill={token('color.background.accent.blue.subtler', '#4C9AFF')}
				/>

				<rect
					x="239"
					y="438"
					width="16"
					height="16"
					rx="4"
					fill={token('color.background.accent.blue.subtle', '#4C9AFF')}
				/>
				<rect
					x="377"
					y="441"
					width="120"
					height="8"
					rx="4"
					fill={token('color.background.accent.gray.subtler', '#DFE1E6')}
				/>
				<rect
					x="527"
					y="438"
					width="64"
					height="16"
					rx="3"
					fill={token('color.background.accent.green.subtler', '#ABF5D1')}
				/>
				<rect
					x="616"
					y="436"
					width="20"
					height="20"
					rx="10"
					fill={token('color.background.accent.orange.subtler', '#FFC400')}
				/>
				<rect
					x="224"
					y="102"
					width="180"
					height="16"
					rx="8"
					fill={token('color.background.accent.gray.subtler', '#DFE1E6')}
				/>
				<rect
					x="624"
					y="102"
					width="16"
					height="16"
					rx="8"
					fill={token('color.background.accent.gray.subtler', '#DFE1E6')}
				/>
				<rect
					x="600"
					y="102"
					width="16"
					height="16"
					rx="8"
					fill={token('color.background.accent.gray.subtler', '#DFE1E6')}
				/>
				<rect
					x="576"
					y="102"
					width="16"
					height="16"
					rx="8"
					fill={token('color.background.accent.gray.subtler', '#DFE1E6')}
				/>

				<rect
					x="226"
					y="172"
					width="420"
					height="2"
					fill={token('color.border.disabled', '#EBECF0')}
				/>
				<rect
					x="226"
					y="214"
					width="420"
					height="2"
					fill={token('color.border.disabled', '#EBECF0')}
				/>
				<rect
					x="226"
					y="256"
					width="420"
					height="2"
					fill={token('color.border.disabled', '#EBECF0')}
				/>
				<rect
					x="226"
					y="298"
					width="420"
					height="2"
					fill={token('color.border.disabled', '#EBECF0')}
				/>
				<rect
					x="226"
					y="340"
					width="420"
					height="2"
					fill={token('color.border.disabled', '#EBECF0')}
				/>
				<rect
					x="226"
					y="382"
					width="420"
					height="2"
					fill={token('color.border.disabled', '#EBECF0')}
				/>
				<rect
					x="226"
					y="424"
					width="420"
					height="2"
					fill={token('color.border.disabled', '#EBECF0')}
				/>

				<ProjectNameText x={72} y={103}>
					{projectName}
				</ProjectNameText>
				<text fontSize="14" x="280" y="198" fill={token('color.text.subtle', '#7A869A')}>
					{transformProjectKeyText(projectKey, 1)}
				</text>
				<text fontSize="14" x="280" y="240" fill={token('color.text.subtle', '#7A869A')}>
					{transformProjectKeyText(projectKey, 2)}
				</text>
				<text fontSize="14" x="280" y="282" fill={token('color.text.subtle', '#7A869A')}>
					{transformProjectKeyText(projectKey, 3)}
				</text>
				<text fontSize="14" x="280" y="324" fill={token('color.text.subtle', '#7A869A')}>
					{transformProjectKeyText(projectKey, 4)}
				</text>
				<text fontSize="14" x="280" y="366" fill={token('color.text.subtle', '#7A869A')}>
					{transformProjectKeyText(projectKey, 5)}
				</text>
				<text fontSize="14" x="280" y="408" fill={token('color.text.subtle', '#7A869A')}>
					{transformProjectKeyText(projectKey, 6)}
				</text>
				<text fontSize="14" x="280" y="450" fill={token('color.text.subtle', '#7A869A')}>
					{transformProjectKeyText(projectKey, 7)}
				</text>
				<text
					fontSize="12"
					x="110"
					y="460"
					textAnchor="middle"
					fill={token('color.text.subtlest', '#6B778C')}
				>
					{isTmp
						? formatMessage(teamManagedIllustrationFooter)
						: formatMessage(companyManagedIllustrationFooter)}
				</text>
				<defs>
					<filter id="create-project-shadow">
						<feDropShadow dx="2" dy="2" stdDeviation="10" floodColor="#000" floodOpacity="0.2" />
					</filter>
				</defs>
			</svg>
		</IllustrationWrapper>
	);
};
